<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { languageIdBus } from '@/event/languageIdBus'
import { productBus } from '@/event/product/productBus'
import { relatedGroupBus } from '@/event/product/relatedGroupeBus'
import { showSelectedModalProductHappy } from '@/happyTheme/event/product/showSelectedModalProductHappy'
import { toggleSelectedModalProductHappy } from '@/happyTheme/utils/product/toggleSelectedModalProductHappy'
import { excludeRouteDropdownCategoryArray } from '@/storage/navbar/excludeRoute'
import { getOptionName } from '@/utils/product/getOptionName'
import { getProductDescription } from '@/utils/product/getProductDescription'
import { getProductName } from '@/utils/product/getProductName'
import { hasDescriptionForLanguage } from '@/utils/product/hasDescriptionForLanguage'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import SelectOptionProductHappy from './SelectOptionProductHappy.vue'
import InputQuantityProductHappy from '@/happyTheme/components/product/InputQuantityProductHappy.vue'
import { type SelectedOptionHappy } from '@/happyTheme/interfaces/product/selectedOptionInterface'
import { selectOptionsListHappy } from '@/happyTheme/event/product/selectOptionsListHappy'
import { openStatesHappy } from '@/happyTheme/event/product/openStatesHappy'
import { getEmptySelectedValuesHappy } from '@/happyTheme/utils/product/modal/getEmptySelectedValuesHappy'
import {
    messageErrorSelectOptionHappy,
    messageErrorSelectOptionHappyEmpty
} from '@/happyTheme/event/product/messageErrorSelectOptionHappy'
import { visibleOptionsHappy } from '@/happyTheme/event/product/visibleOptionHappy'
import MessageErrorOptionHappy from '@/happyTheme/components/product/MessageErrorOptionHappy.vue'
import { selectedPriceProductHappy } from '@/happyTheme/utils/product/modal/selectedPriceProductHappy'
import { addToCartHappy } from '@/happyTheme/utils/product/modal/addToCartHappy'
import { showBannerMessageSpecial } from '@/happyTheme/event/messageSpecial/showBannerMessageSpecial'
import { showHeader } from '@/happyTheme/event/layout/showHeader'
import { TimeOutClass } from '@/happyTheme/services/timeout/TimeOutClass'
import { productAssociated } from '@/event/product/productAssociated'
import ProductAssociated from '@/components/product/ProductAssociated.vue'
import ButtonBasketProductHappy from '@/happyTheme/components/product/ButtonBasketProductHappy.vue'
import { showAlertValidateProductHappy } from '@/happyTheme/event/product/showAlertValidateProductHappy'
import { showProductAssociated } from '@/happyTheme/event/product/showProductAssociated'
import { groupByNameEnhancedHappy } from '@/happyTheme/utils/product/groupeByNameEnhancedHappy'
import { X, ChevronDown } from 'lucide-vue-next'
import { formatPrice } from '@/event/locale'

const route = useRoute()
const optionRefs = ref<Record<number, HTMLElement>>({})
const resizeObservers = ref<Record<number, ResizeObserver>>({})

const heightBottomModal = ref(64)

const setOptionRef = (el: HTMLElement | null, index: number) => {
    if (el) {
        optionRefs.value[index] = el

        if (!resizeObservers.value[index]) {
            resizeObservers.value[index] = new ResizeObserver(() => {
                updateHeightForIdx(index)
            })
        }
        resizeObservers.value[index].observe(el)
    } else {
        if (resizeObservers.value[index]) {
            resizeObservers.value[index].disconnect()
            delete resizeObservers.value[index]
        }
        delete optionRefs.value[index]
    }
}

const handleScroll = () => {
    const modalBody = document.getElementById('modal-body-product-selected-happy')
    if (!modalBody) return

    const viewportHeight = window.innerHeight
    const viewportCenter = viewportHeight / 2

    if (
        !visibleOptionsHappy.value ||
        visibleOptionsHappy.value.length !== productBus.value?.options.length
    ) {
        visibleOptionsHappy.value = new Array(productBus.value?.options.length || 0).fill(
            false
        )
    } else {
        visibleOptionsHappy.value = visibleOptionsHappy.value.map(() => false)
    }

    let closestElement = null
    let closestDistance = Infinity

    const openedIndices: number[] = []

    openStatesHappy.value.forEach((isOpen, idx) => {
        if (!isOpen) return

        openedIndices.push(idx)

        const optionElement = document.querySelector(
            `.div-v-for-option-product-happy[data-index="${idx}"]`
        ) as HTMLElement | null
        if (!optionElement) return

        const rect = optionElement.getBoundingClientRect()
        const elementTop = rect.top
        const elementHeight = rect.height
        const elementBottom = rect.bottom
        const elementCenter = elementTop + elementHeight / 2

        const isVisible = elementBottom > 0 && elementTop < viewportHeight
        if (!isVisible) return

        const distanceFromCenter = Math.abs(viewportCenter - elementCenter)

        if (distanceFromCenter < closestDistance) {
            closestDistance = distanceFromCenter
            closestElement = idx
        }
    })

    if (openedIndices.length) {
        const firstIdx = openedIndices[0]
        const lastIdx = openedIndices[openedIndices.length - 1]

        const firstElement = document.querySelector(
            `.div-v-for-option-product-happy[data-index="${firstIdx}"]`
        )
        const lastElement = document.querySelector(
            `.div-v-for-option-product-happy[data-index="${lastIdx}"]`
        )

        if (firstElement && firstElement.getBoundingClientRect().top > viewportHeight) {
            closestElement = firstIdx
        } else if (lastElement && lastElement.getBoundingClientRect().bottom < 0) {
            closestElement = lastIdx
        }
    }

    if (closestElement !== null) {
        visibleOptionsHappy.value[closestElement] = true
    }
}

function selectedOption(option: SelectedOptionHappy) {
    selectOptionsListHappy.value[option.option_id] = option
}

function getTotalSelectedQuantityRadio(optionId: number): number {
    if (selectOptionsListHappy.value[optionId]) {
        const radioOptionId =
            selectOptionsListHappy.value[optionId].selected_values.radio_option
                .option_value_id

        return radioOptionId ? 1 : 0
    }

    return 0
}

function getTotalSelectedQuantitySelect(optionId: number) {
    let result = {
        freeQuantity: 0,
        paidQuantity: 0,
        freeActiveQuantity: 0,
        paidActiveQuantity: 0
    }

    if (!selectOptionsListHappy.value[optionId]) {
        return result
    }

    const option = productBus.value?.options.find((product) => {
        return product.option_id === optionId
    })

    if (option?.type === 'select' || option?.type === 'checkbox') {
        const countResetFree = selectOptionsListHappy.value[
            optionId
        ].selected_values.checkbox_option.option_value_free.reduce((acc, element) => {
            if (element.option_value_reset !== 1) {
                acc++
            }
            return acc
        }, 0)

        const countResetPaid = selectOptionsListHappy.value[
            optionId
        ].selected_values.checkbox_option.option_value_paid.reduce((acc, element) => {
            if (element.option_value_reset !== 1) {
                acc++
            }
            return acc
        }, 0)

        result.freeActiveQuantity = countResetFree
        result.paidActiveQuantity = countResetPaid
    }

    const resetOption =
        option?.values.filter((o) => o.reset == 1).map((o) => o.option_value_id) || []

    if (
        (option?.free_option_count === 0 && option.max_option_count === 0) ||
        (option?.free_option_count !== 0 && option?.max_option_count === 0)
    ) {
        result.freeQuantity = option.free_option_count
        result.paidQuantity = option.values.length - resetOption?.length
        return result
    }

    if (
        option?.free_option_count !== 0 &&
        option?.max_option_count &&
        option?.max_option_count !== 0 &&
        option?.free_option_count
    ) {
        result.freeQuantity = 0
        result.paidQuantity = option?.max_option_count - option?.free_option_count || 0
        return result
    }

    return result
}

function updateHeightForIdx(idx: number) {
    const openedIndices = openStatesHappy.value
        .map((isOpen, i) => (isOpen ? i : -1))
        .filter((i) => i >= 0)

    let maxHeight = 64

    openedIndices.forEach((openedIdx) => {
        const openedEl = optionRefs.value[openedIdx]
        if (openedEl) {
            const h = openedEl.getBoundingClientRect().height || 0
            maxHeight = Math.max(maxHeight, Math.round(h + 72))
        }
    })

    window.requestAnimationFrame(() => {
        heightBottomModal.value = maxHeight
    })
}

async function toggleShowDropdownOption(index: number) {
    openStatesHappy.value[index] = !openStatesHappy.value[index]

    visibleOptionsHappy.value[index] = openStatesHappy.value[index]

    await nextTick()
    updateHeightForIdx(index)
}

const getEnhancedGroupedOptions = (optionId: number) => {
    if (!selectOptionsListHappy.value[optionId]) return []

    const option = productBus.value?.options.find((opt) => opt.option_id === optionId)
    if (!option) return []

    const allOptions = []

    // Radio option
    if (
        option.type === 'radio' &&
        selectOptionsListHappy.value[optionId].selected_values.radio_option
            .option_value_id
    ) {
        if (
            Number(
                selectOptionsListHappy.value[optionId].selected_values.radio_option
                    .option_value_price
            ) > 0
        ) {
            allOptions.push({
                id: `radio-${optionId}`,
                name: selectOptionsListHappy.value[optionId].selected_values.radio_option
                    .option_value_name,
                type: 'radio',
                count: 1,
                price:
                    selectOptionsListHappy.value[optionId].selected_values.radio_option
                        .option_value_price || 0
            })
        } else {
            allOptions.push({
                id: `radio-${optionId}`,
                name: selectOptionsListHappy.value[optionId].selected_values.radio_option
                    .option_value_name,
                type: 'radio',
                count: 1,
                price:
                    selectOptionsListHappy.value[optionId].selected_values.radio_option
                        .option_value_price || 0
            })
        }
    }

    // Free options
    if (
        (option.type === 'select' || option.type === 'checkbox') &&
        selectOptionsListHappy.value[optionId].selected_values.checkbox_option
            .option_value_free.length > 0
    ) {
        const freeOptions =
            selectOptionsListHappy.value[optionId].selected_values.checkbox_option
                .option_value_free
        const groupedFree = groupByNameEnhancedHappy(freeOptions, 'free', optionId)
        allOptions.push(...groupedFree)
    }

    // Paid options
    if (
        (option.type === 'select' || option.type === 'checkbox') &&
        selectOptionsListHappy.value[optionId].selected_values.checkbox_option
            .option_value_paid.length > 0
    ) {
        const paidOptions =
            selectOptionsListHappy.value[optionId].selected_values.checkbox_option
                .option_value_paid
        const groupedPaid = groupByNameEnhancedHappy(paidOptions, 'paid', optionId)
        allOptions.push(...groupedPaid)
    }

    return allOptions
}

watch(
    () => productBus.value?.options,
    (opts) => {
        selectOptionsListHappy.value = {}
        if (!opts) return
        opts.forEach((option) => {
            selectOptionsListHappy.value[option.option_id] =
                getEmptySelectedValuesHappy(option)
        })

        openStatesHappy.value = new Array(opts.length).fill(false)
        visibleOptionsHappy.value = new Array(opts.length).fill(false)

        messageErrorSelectOptionHappy.value = new Array(opts.length).fill(
            messageErrorSelectOptionHappyEmpty
        )

        if (opts.length > 0) {
            openStatesHappy.value[0] = true
            visibleOptionsHappy.value[0] = true
        }
    },
    { immediate: true }
)

watch(
    () => showSelectedModalProductHappy.value,
    (isVisible) => {
        if (!isVisible) {
            const html = document.documentElement
            if (html.classList.contains('modal-open')) {
                html.classList.remove('modal-open')
            }
        }
    }
)

watch(
    () => route.name,
    (routeName) => {
        if (routeName) {
            const routeNameString = String(routeName)
            if (!excludeRouteDropdownCategoryArray.includes(routeNameString)) {
                showSelectedModalProductHappy.value = false
            }
        }
    },
    {
        deep: true
    }
)

watch(
    openStatesHappy,
    async () => {
        await nextTick()

        const opened = openStatesHappy.value
            .map((isOpen, idx) => (isOpen ? idx : -1))
            .filter((idx) => idx >= 0)

        if (opened.length === 0) {
            heightBottomModal.value = 64
            visibleOptionsHappy.value = visibleOptionsHappy.value.map(() => false)
            return
        }
    },
    { deep: true, immediate: true }
)

onMounted(() => {
    showAlertValidateProductHappy.value = false
    showProductAssociated.value = false
    openStatesHappy.value = openStatesHappy.value.map(() => false)
    handleScroll()
    showHeader.value = true

    const idTimeOut = setTimeout(() => {
        showSelectedModalProductHappy.value = true

        nextTick(() => {
            const modalBody = document.getElementById('modal-body-product-selected-happy')
            if (modalBody) {
                modalBody.addEventListener('scroll', handleScroll)
            }
        })
    }, 200)
    const timeOutClass = TimeOutClass.getInstance()
    timeOutClass.setTimeoutWithKey('showModalProductHappy', idTimeOut)

    if (!productBus.value) return
    productBus.value.options.forEach((option) => {
        if (!selectOptionsListHappy.value[option.option_id]) {
            selectOptionsListHappy.value[option.option_id] =
                getEmptySelectedValuesHappy(option)
        }
    })
    visibleOptionsHappy.value = new Array(productBus.value.options.length).fill(false)

    if (productBus.value.options.length > 0) {
        openStatesHappy.value[0] = true
        visibleOptionsHappy.value[0] = true
    }
})

onBeforeUnmount(() => {
    resizeObservers.value = {}
    window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
    <section
        class="section-modal-product-selected-happy"
        v-if="productBus && openStatesHappy.length === productBus.options.length"
    >
        <!-- <Transition
            name="slide-overlay-product"
            enter-active-class="transition-transform duration-300 ease-out"
            enter-from-class="translate-x-full"
            enter-to-class="translate-x-0"
            leave-active-class="transition-transform duration-300 ease-out"
            leave-from-class="translate-x-0"
            leave-to-class="translate-x-full"
        > -->
        <section
            @click.stop="toggleSelectedModalProductHappy()"
            v-if="showSelectedModalProductHappy"
            class="overlay-section-product-happy"
        ></section>
        <!-- </Transition> -->

        <!-- <Transition
            name="slide-modal-product"
            enter-active-class="transition-transform duration-300 ease-out"
            enter-from-class="translate-x-full"
            enter-to-class="translate-x-0"
            leave-active-class="transition-transform duration-300 ease-out"
            leave-from-class="translate-x-0"
            leave-to-class="translate-x-full"
        > -->
        <section
            class="section-selected-product-happy"
            :class="{
                'padding-with-banner': showBannerMessageSpecial,
                'padding-without-banner': !showBannerMessageSpecial,
                'py-2': productBus.hasCleanImg
            }"
            v-if="showSelectedModalProductHappy"
        >
            <div class="div-button-close-product-selected-happy">
                <button
                    class="close-modal-product-selected-happy"
                    @click="toggleSelectedModalProductHappy"
                >
                    <X
                        :size="22"
                        :stroke-width="2"
                        class="svg-cross-product-selected-happy"
                    />
                </button>
            </div>

            <section
                class="modal-body-product-selected-happy"
                id="modal-body-product-selected-happy"
                v-if="!showProductAssociated"
            >
                <section class="section-product-selected-happy">
                    <div
                        class=""
                        :class="{
                            'div-product-selected-happy': productBus.hasCleanImg,
                            'div-product-selected-img-with-bg': !productBus.hasCleanImg
                        }"
                    >
                        <img
                            loading="lazy"
                            class="img-product-selected-happy"
                            :class="{
                                'object-cover min-w-full h-60': !productBus.hasCleanImg
                            }"
                            :src="`${URL_IMAGE}${productBus.image}`"
                            alt=""
                        />
                    </div>

                    <div class="div-product-information-selected-happy">
                        <h3 class="h3-product-selected-happy">
                            {{ getProductName(productBus) }}
                        </h3>

                        <p
                            v-if="
                                hasDescriptionForLanguage(
                                    productBus.description,
                                    languageIdBus
                                )
                            "
                        >
                            {{ getProductDescription(productBus) }}
                        </p>
                    </div>
                </section>

                <section
                    class="section-product-option-selected-happy"
                    v-if="productBus.options.length > 0"
                >
                    <div class="div-product-option-information-happy">
                        <h4 class="h4-product-option-selected-happy">{{ $t('options') }}</h4>

                        <p class="text-obligation-option-selected-happy">
                            <span class="text-sm">*</span>{{ $t('required') }}
                        </p>
                    </div>
                    <section class="div-form-product-happy">
                        <div
                            v-for="(option, idx) in productBus.options"
                            :key="option.option_id"
                            :id="String(option.option_id)"
                            class="w-full"
                            :class="{
                                hidden: relatedGroupBus.includes(option.option_id)
                            }"
                        >
                            <div
                                class="div-v-for-option-product-happy"
                                :data-index="idx"
                                :class="{
                                    shake:
                                        messageErrorSelectOptionHappy[idx].type ===
                                        'error',
                                    'border-red-eronor':
                                        messageErrorSelectOptionHappy[idx].type ===
                                        'error'
                                }"
                            >
                                <div
                                    v-if="!relatedGroupBus.includes(option.option_id)"
                                    :class="{
                                        'border-title-option-happy': openStatesHappy[idx]
                                    }"
                                    @click.stop="toggleShowDropdownOption(idx)"
                                >
                                    <div class="form-option-title-product-happy">
                                        <h4
                                            :id="getOptionName(option, languageIdBus)"
                                            v-if="
                                                option.required &&
                                                (option.type === 'select' ||
                                                    option.type === 'checkbox')
                                            "
                                            class="h4-option-happy"
                                        >
                                            <p class="">
                                                {{ getOptionName(option, languageIdBus)
                                                }}<span
                                                    v-if="option.required"
                                                    class="choice"
                                                    >*</span
                                                >
                                            </p>
                                        </h4>

                                        <h4
                                            v-else-if="
                                                option.type === 'radio' &&
                                                !option.required
                                            "
                                            :id="getOptionName(option, languageIdBus)"
                                            class="h4-option-happy"
                                        >
                                            {{ getOptionName(option, languageIdBus) }}
                                        </h4>

                                        <h4
                                            v-else-if="
                                                option.type === 'radio' && option.required
                                            "
                                            :id="getOptionName(option, languageIdBus)"
                                            class="h4-option-happy"
                                        >
                                            {{ getOptionName(option, languageIdBus)
                                            }}<span class="choice">*</span>
                                        </h4>

                                        <h4
                                            v-else
                                            :id="getOptionName(option, languageIdBus)"
                                            class="h4-option-happy"
                                        >
                                            {{ getOptionName(option, languageIdBus) }}
                                        </h4>

                                        <ChevronDown
                                            :size="20"
                                            :stroke-width="2"
                                            class="svg-arrow-happy-option-product-happy"
                                            :class="{
                                                'rotate-180': openStatesHappy[idx],
                                                'pb-0.5': !openStatesHappy[idx]
                                            }"
                                        />
                                    </div>

                                    <div
                                        v-if="
                                            selectOptionsListHappy[option.option_id] &&
                                            (selectOptionsListHappy[option.option_id]
                                                .selected_values.radio_option
                                                .option_value_id !== 0 ||
                                                selectOptionsListHappy[option.option_id]
                                                    .selected_values.checkbox_option
                                                    .option_value_free.length > 0 ||
                                                selectOptionsListHappy[option.option_id]
                                                    .selected_values.checkbox_option
                                                    .option_value_paid.length > 0)
                                        "
                                        class="option-selected-happy"
                                    >
                                        <TransitionGroup
                                            name="option-cascade"
                                            tag="div"
                                            enter-active-class="transition-all duration-400 ease-out"
                                            enter-from-class="opacity-0 transform translate-x-4 scale-90"
                                            enter-to-class="opacity-100 transform translate-x-0 scale-100"
                                            leave-active-class="transition-all duration-300 ease-in"
                                            leave-from-class="opacity-100 transform translate-x-0 scale-100"
                                            leave-to-class="opacity-0 transform -translate-x-4 scale-90"
                                            move-class="transition-none"
                                        >
                                            <div
                                                v-for="groupedOption in getEnhancedGroupedOptions(
                                                    option.option_id
                                                )"
                                                :key="groupedOption.id"
                                                class="grouped-option-item"
                                                :class="{
                                                    'option-paid':
                                                        groupedOption.type === 'paid',
                                                    'option-free':
                                                        groupedOption.type === 'free',
                                                    'option-radio':
                                                        groupedOption.type === 'radio'
                                                }"
                                            >
                                                <span class="option-name">
                                                    - {{ groupedOption.name }}
                                                </span>

                                                <!-- ✅ Badge pour la quantité -->
                                                <span
                                                    v-if="groupedOption.count > 1"
                                                    class="quantity-badge"
                                                >
                                                    x{{ groupedOption.count }}
                                                </span>

                                                <!-- ✅ Prix total si payant -->
                                                <span
                                                    v-if="
                                                        groupedOption.type === 'paid' &&
                                                        'totalPrice' in groupedOption &&
                                                        groupedOption.totalPrice > 0
                                                    "
                                                    class="price-info-happy"
                                                >
                                                    (+{{ formatPrice(groupedOption.totalPrice) }})
                                                </span>

                                                <span
                                                    v-if="
                                                        groupedOption.type === 'radio' &&
                                                        Number(groupedOption.price) > 0
                                                    "
                                                    class="price-info-happy"
                                                >
                                                    (+{{ formatPrice(Number(groupedOption.price)) }})
                                                </span>
                                            </div>
                                        </TransitionGroup>
                                    </div>

                                    <MessageErrorOptionHappy
                                        v-if="
                                            messageErrorSelectOptionHappy[idx].message &&
                                            messageErrorSelectOptionHappy[idx].type
                                        "
                                        :message="
                                            messageErrorSelectOptionHappy[idx].message
                                        "
                                        :type="messageErrorSelectOptionHappy[idx].type"
                                    />
                                </div>

                                <div
                                    class="dropdown-container"
                                    :class="{
                                        'dropdown-open': openStatesHappy[idx],
                                        'dropdown-closed': !openStatesHappy[idx]
                                    }"
                                >
                                    <SelectOptionProductHappy
                                        v-if="openStatesHappy[idx]"
                                        :options="option"
                                        :index="idx"
                                        :name_option="
                                            getOptionName(option, languageIdBus)
                                        "
                                        @select-option="
                                            (opt: SelectedOptionHappy) =>
                                                selectedOption(opt)
                                        "
                                    />
                                </div>
                            </div>

                            <div
                                v-if="openStatesHappy[idx]"
                                :ref="(el) => setOptionRef(el as HTMLElement, idx)"
                                class="div-option-selected-happy"
                                :data-index="idx"
                                :class="{
                                    'fixed-bottom ':
                                        openStatesHappy[idx] && visibleOptionsHappy[idx],
                                    'fixed -bottom-full opacity-0':
                                        openStatesHappy[idx] && !visibleOptionsHappy[idx]
                                }"
                            >
                                <div>
                                    <p
                                        class="font-semibold"
                                        v-if="visibleOptionsHappy[idx]"
                                    >
                                        {{ getOptionName(option, languageIdBus) }}
                                    </p>
                                    <span
                                        v-if="
                                            option.values.find(
                                                (e) => e.price_status === 'special'
                                            )
                                        "
                                        class="text-xs italic font-bold text-border-red-happy-light"
                                        >{{ $t('someOptionsPaid') }}
                                    </span>
                                    <span
                                        class="span-choice-happy-required"
                                        v-if="option.type === 'radio' && option.required"
                                    >
                                        <p>
                                            {{
                                                getTotalSelectedQuantityRadio(
                                                    option.option_id
                                                )
                                            }}/1 gratuit
                                        </p>
                                    </span>
                                    <span
                                        class="span-choice-happy"
                                        v-if="option.type === 'radio' && !option.required"
                                    >
                                        <p>
                                            {{
                                                getTotalSelectedQuantityRadio(
                                                    option.option_id
                                                )
                                            }}/1 gratuit
                                        </p>
                                    </span>
                                    <span
                                        v-if="
                                            (option.type === 'select' ||
                                                option.type === 'checkbox') &&
                                            option.required
                                        "
                                    >
                                        <div
                                            class="span-choice-happy-required"
                                            v-if="
                                                option.free_option_count === 0 &&
                                                option.max_option_count === 0
                                            "
                                        >
                                            <p>
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity
                                                }}
                                                {{ $t('maximum') }}
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count > 0 &&
                                                option.max_option_count === 0
                                            "
                                        >
                                            <p class="span-choice-happy-required">
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).freeActiveQuantity
                                                }}/{{ option.free_option_count }}
                                                {{ $t('free') }}
                                            </p>
                                            <p
                                                class="span-choice-happy"
                                                v-if="
                                                    option.max_option_count -
                                                        option.free_option_count !==
                                                    0
                                                "
                                            >
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity -
                                                    option.free_option_count
                                                }}
                                                {{ $t('paid') }}
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count > 0 &&
                                                option.max_option_count > 0
                                            "
                                        >
                                            <p class="span-choice-happy-required">
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).freeActiveQuantity
                                                }}/{{ option.free_option_count }}
                                                {{ $t('free') }}
                                            </p>
                                            <p
                                                class="span-choice-happy"
                                                v-if="
                                                    option.max_option_count -
                                                        option.free_option_count !==
                                                    0
                                                "
                                            >
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    option.max_option_count -
                                                    option.free_option_count
                                                }}
                                                {{ $t('paid') }}
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count === 0 &&
                                                option.max_option_count > 0
                                            "
                                        >
                                            <p class="span-choice-happy">
                                                {{
                                                    Math.max(
                                                        getTotalSelectedQuantitySelect(
                                                            option.option_id
                                                        ).paidActiveQuantity
                                                    )
                                                }}/{{
                                                    option.max_option_count -
                                                    option.free_option_count
                                                }}
                                                {{ $t('maximum') }}
                                            </p>
                                        </div>
                                    </span>
                                    <span
                                        class="span-choice-happy"
                                        v-if="
                                            (option.type === 'select' ||
                                                option.type === 'checkbox') &&
                                            !option.required
                                        "
                                    >
                                        <div
                                            class="span-choice-happy-required"
                                            v-if="
                                                option.free_option_count === 0 &&
                                                option.max_option_count === 0
                                            "
                                        >
                                            <p>
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity
                                                }}
                                                {{ $t('maximum') }}
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count > 0 &&
                                                option.max_option_count === 0
                                            "
                                        >
                                            <p class="span-choice-happy-required">
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).freeActiveQuantity
                                                }}/{{ option.free_option_count }}
                                                {{ $t('free') }}
                                            </p>
                                            <p
                                                class="span-choice-happy"
                                                v-if="
                                                    option.max_option_count -
                                                        option.free_option_count !==
                                                    0
                                                "
                                            >
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity -
                                                    option.free_option_count
                                                }}
                                                {{ $t('paid') }}
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selecti-option-happy"
                                            v-if="
                                                option.free_option_count > 0 &&
                                                option.max_option_count > 0
                                            "
                                        >
                                            <p class="span-choice-happy-required">
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).freeActiveQuantity
                                                }}/{{ option.free_option_count }}
                                                {{ $t('free') }}
                                            </p>
                                            <p
                                                class="span-choice-happy"
                                                v-if="
                                                    option.max_option_count -
                                                        option.free_option_count !==
                                                    0
                                                "
                                            >
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity
                                                }}
                                                {{ $t('paid') }}
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count === 0 &&
                                                option.max_option_count > 0
                                            "
                                        >
                                            <p class="span-choice-happy">
                                                {{
                                                    Math.max(
                                                        getTotalSelectedQuantitySelect(
                                                            option.option_id
                                                        ).paidActiveQuantity
                                                    )
                                                }}/{{
                                                    option.max_option_count -
                                                    option.free_option_count
                                                }}
                                                {{ $t('maximum') }}
                                            </p>
                                        </div>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div :style="{ minHeight: `${heightBottomModal}px` }"></div>
                </section>
                <div
                    class="modal-footer-product-happy"
                    :class="{
                        'fixed bottom-0 items-center': productBus.options.length > 0,
                        'flex-grow h-screen items-start': productBus.options.length === 0
                    }"
                >
                    <InputQuantityProductHappy />
                    <div class="relative flex items-center justify-center">
                        <button
                            class="button-add-to-cart-happy"
                            @click="addToCartHappy()"
                        >
                            {{ $t('addToCart') }} {{ formatPrice(selectedPriceProductHappy(productBus.price)) }}
                        </button>
                        <ButtonBasketProductHappy v-if="showAlertValidateProductHappy" />
                    </div>
                </div>
            </section>

            <section
                v-if="productAssociated && showProductAssociated"
                class="section-product-associated-happy"
            >
                <ProductAssociated />
                <div :style="{ minHeight: `${heightBottomModal}px` }"></div>
            </section>
        </section>
        <!-- </Transition> -->
    </section>
</template>

<style scoped>
.section-modal-product-selected-happy {
    @apply z-20 fixed top-0 w-screen  overflow-y-auto flex justify-end;
    height: 100%;
}

.overlay-section-product-happy {
    /* refactored */
    @apply fixed top-0 w-screen h-full opacity-70 bg-black;
    @apply happy-ifo-dark:bg-black happy-ifo-dark:opacity-80;
}

.section-selected-product-happy {
    /* refactored */
    @apply relative w-full min-h-screen;
    @apply flex flex-col z-10 bg-t-bg;
    @apply happy-ifo-dark:bg-t-bg happy-ifo-dark:border-l happy-ifo-dark:border-t-accent/20;
    @apply happy-purple-gradient:bg-gradient-to-t happy-purple-gradient:to-bg-purple-dark happy-purple-gradient:from-bg-purple-light;
}

.padding-without-banner {
    padding-top: var(--navbar-height, 5rem);
}

.padding-with-banner {
    padding-top: var(--navbar-banner-total-height, 5rem);
}

.div-button-close-product-selected-happy {
    /* refactored — daha kompakt kapatma butonu (36x36px touch-friendly,
       22px X icon). Önceki 40px icon + p-1 = çok büyüktü. rounded-full
       daha modern görünüm. */
    @apply fixed flex items-center justify-center w-9 h-9 shrink-0 z-10 mt-3 ml-3 rounded-full shadow-sm;
    @apply bg-t-accent;
    @apply happy-ifo-dark:bg-t-card happy-ifo-dark:shadow-black/30 happy-ifo-dark:border happy-ifo-dark:border-t-accent/25;
}

.svg-cross-product-selected-happy {
    /* refactored: Lucide uses currentColor */
    @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
}

.div-button-close-product-selected-happy:active,
.div-button-close-product-selected-happy:focus {
    /* refactored */
    @apply bg-t-accent-hover;
    @apply happy-ifo-dark:bg-t-accent-hover;
}

.div-button-close-product-selected-happy:active .svg-cross-product-selected-happy,
.div-button-close-product-selected-happy:focus .svg-cross-product-selected-happy {
    /* refactored: Lucide uses currentColor */
    @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-text;
}

.modal-body-product-selected-happy {
    /* refactored */
    @apply flex flex-col overflow-y-auto bg-t-bg;
    @apply happy-ifo-dark:bg-t-bg;
}

.section-product-selected-happy {
    /* refactored */
    @apply max-w-full flex flex-col bg-t-bg;
    @apply happy-ifo-dark:bg-t-bg;
    min-height: fit-content;
}

.div-product-selected-happy {
    /* refactored */
    @apply w-full flex flex-col items-center gap-2 mb-2 bg-t-bg;
    @apply happy-ifo-dark:bg-t-bg;
}
.div-product-selected-img-with-bg {
    @apply w-full flex pt-16;
    @apply happy-ifo-dark:pt-10 happy-ifo-dark:justify-center;
}
.img-product-selected-happy {
    @apply max-w-80;
    @apply happy-ifo-dark:max-w-44 happy-ifo-dark:rounded-lg;
    @apply happy-ifo-dark:drop-shadow-[0_0_25px_rgba(212,160,23,0.3)];
}

.div-product-information-selected-happy {
    @apply w-full flex flex-col gap-2 p-2;
    @apply happy-ifo-dark:px-4 happy-ifo-dark:pt-3;
}

.h3-product-selected-happy {
    @apply font-bold text-3xl;
    @apply happy-ifo-dark:text-2xl happy-ifo-dark:text-t-accent;
}

.div-product-option-selected-happy {
    @apply w-full;
}
.section-product-option-selected-happy {
    /* refactored */
    @apply w-full h-full flex flex-col px-2 pt-2;
    @apply bg-t-bg border-t border-t-border;
    @apply happy-ifo-dark:bg-t-bg happy-ifo-dark:border-t happy-ifo-dark:border-t-accent/30 happy-ifo-dark:px-4;
}

.div-product-option-information-happy {
    @apply w-full flex justify-between items-center;
}

.h4-product-option-selected-happy {
    /* refactored */
    @apply font-semibold text-lg pb-2 text-t-text;
    @apply happy-ifo-dark:text-t-accent;
}

.text-obligation-option-selected-happy {
    /* refactored */
    @apply text-xs font-semibold text-t-accent;
}

.div-form-product-happy {
    @apply flex flex-col gap-3;
}

.div-v-for-option-product-happy {
    /* refactored */
    @apply rounded-xl border-2 w-full;
    @apply bg-t-card border-t-border text-t-text;
    @apply happy-ifo-dark:border happy-ifo-dark:rounded-lg happy-ifo-dark:bg-t-card happy-ifo-dark:shadow-md happy-ifo-dark:shadow-black/20 happy-ifo-dark:border-t-accent/30 happy-ifo-dark:text-t-text;
}

.form-option-title-product-happy {
    @apply flex flex-row items-center justify-between px-3 py-2 cursor-pointer;
    @apply happy-ifo-dark:transition-colors happy-ifo-dark:duration-200 happy-ifo-dark:rounded-t-lg;
}
@media (hover: hover) {
    .form-option-title-product-happy:hover {
        @apply happy-ifo-dark:bg-t-accent/5;
    }
}

.h4-option-happy {
    @apply flex font-semibold whitespace-pre-wrap;
    @apply happy-ifo-dark:text-t-accent;
}
.choice {
    /* refactored */
    @apply text-t-accent;
}

.svg-arrow-happy-option-product-happy {
    /* refactored: Lucide uses currentColor */
    @apply min-w-5 min-h-5 max-w-5 max-h-5 transition-transform duration-300 text-t-text;
    @apply happy-ifo-dark:text-t-accent;
}

.border-title-option-happy {
    /* refactored */
    @apply border-b border-t-border;
    @apply happy-ifo-dark:border-t-accent/20;
}

.option-selected-happy {
    /* refactored */
    @apply px-3 py-1 text-sm font-semibold border-t border-t-border;
    @apply happy-ifo-dark:border-t-0 happy-ifo-dark:text-t-accent;
}
.option-selected-happy > div {
    @apply happy-ifo-dark:flex happy-ifo-dark:flex-wrap happy-ifo-dark:gap-1.5 happy-ifo-dark:py-1;
}
.option-selected-happy .grouped-option-item {
    @apply happy-ifo-dark:inline-flex happy-ifo-dark:items-center happy-ifo-dark:gap-1 happy-ifo-dark:px-2.5 happy-ifo-dark:py-0.5 happy-ifo-dark:rounded-full happy-ifo-dark:bg-t-accent/10 happy-ifo-dark:border happy-ifo-dark:border-t-accent/30 happy-ifo-dark:text-xs;
}

.price-info-happy {
    /* refactored */
    @apply font-semibold text-t-accent;
}
.dropdown-container {
    overflow: hidden;
    transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    transform-origin: top;
}

.dropdown-closed {
    max-height: 0;
    opacity: 0;
}

.dropdown-open {
    max-height: 1200px;
    opacity: 1;
}

.fixed-bottom {
    /* refactored */
    @apply fixed rounded-none bottom-[56px] border-b w-full z-30 py-4 px-3 right-0;
    @apply flex items-end justify-between;
    @apply bg-t-card text-t-text border-t-border;
    @apply happy-ifo-dark:text-t-accent happy-ifo-dark:border-t-accent/20 happy-ifo-dark:bg-t-card happy-ifo-dark:border-l happy-ifo-dark:border-b-0 happy-ifo-dark:border-t-0;
}

.span-choice-happy-required {
    /* refactored */
    @apply font-semibold text-t-text;
}
.span-choice-happy {
    /* refactored */
    @apply font-semibold text-t-accent;
}
.div-number-selecti-option-happy {
    @apply flex gap-2;
}

.modal-footer-product-happy {
    /* refactored */
    @apply w-full flex justify-between gap-2 py-2 px-3 z-30 bg-t-card;
    @apply happy-ifo-dark:bg-t-card happy-ifo-dark:border-t happy-ifo-dark:border-t-accent/20 happy-ifo-dark:px-4 happy-ifo-dark:py-3;
}

.button-add-to-cart-happy {
    /* refactored */
    @apply min-w-36 py-2 px-2 rounded-lg font-semibold shadow-sm;
    @apply bg-t-accent text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent happy-ifo-dark:bg-t-accent/10 happy-ifo-dark:border happy-ifo-dark:border-t-accent/30 happy-ifo-dark:shadow-none happy-ifo-dark:font-bold happy-ifo-dark:px-4 happy-ifo-dark:py-2.5 happy-ifo-dark:rounded-full happy-ifo-dark:tracking-wide;
}

.button-add-to-cart-happy:active,
.button-add-to-cart-happy:focus {
    /* refactored */
    @apply bg-t-accent-hover text-t-on-accent;
    @apply happy-ifo-dark:bg-t-accent-hover happy-ifo-dark:text-t-text;
}

.section-product-associated-happy {
    @apply flex flex-col overflow-y-auto pt-14 px-1;

    min-height: fit-content;
}

@media (hover: hover) {
    .div-button-close-product-selected-happy:hover {
        /* refactored */
        @apply bg-t-accent-hover;
        @apply happy-ifo-dark:bg-t-accent-hover;
    }
    .div-button-close-product-selected-happy:hover .svg-cross-product-selected-happy {
        /* refactored: Lucide uses currentColor */
        @apply text-t-on-accent;
        @apply happy-ifo-dark:text-t-text;
    }

    .button-add-to-cart-happy:hover {
        /* refactored */
        @apply bg-t-accent-hover text-t-on-accent;
        @apply happy-ifo-dark:bg-t-accent-hover happy-ifo-dark:text-t-text;
    }
}

@media screen and (min-width: 416px) {
    .section-selected-product-happy {
        @apply max-w-[26rem];
    }
    .div-option-selected-happy {
        @apply max-w-[26rem];
    }
    .modal-footer-product-happy {
        @apply max-w-[26rem];
    }
}

@keyframes shake {
    0% {
        transform: rotate(0deg);
    }
    15% {
        transform: rotate(5deg);
    }
    30% {
        transform: rotate(0eg);
    }
    45% {
        transform: rotate(-5deg);
    }
    60% {
        transform: rotate(0deg);
    }
    75% {
        transform: rotate(-5deg);
    }
    100% {
        transform: rotate(0deg);
    }
}

.shake {
    animation: shake 0.5s ease-in-out;
}
</style>
